import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Microsoft Defender for Endpoint — device health overview (admin, Security hub).
// Reads /api/defender/overview + /devices. Until the Entra app credentials are
// wired into the backend env, the API returns { configured:false } and we show a
// friendly "connect" panel instead of erroring.

const API = process.env.REACT_APP_API_URL;
const headers = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });
const NAVY = '#1A3A5C';
const ORANGE = '#EA6F2E';
const GREEN = '#2E9E5B';
const AMBER = '#D9922E';
const RED = '#D14343';

const card = { background: '#fff', border: '1px solid #EDEDF0', borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };
const label = { fontSize: '11.5px', fontWeight: 600, color: '#8A8A8E', textTransform: 'uppercase', letterSpacing: '0.05em' };

function SectionHeader({ title, subtitle, right }) {
  return (
    <div style={{ marginBottom: '18px', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16 }}>
      <div>
        <h2 style={{ fontSize: '20px', fontWeight: 700, color: NAVY, margin: 0 }}>{title}</h2>
        {subtitle && <p style={{ fontSize: '13px', color: '#6B7280', margin: '4px 0 0' }}>{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

// A colored count "chip row" for a breakdown map { key: n }.
function Breakdown({ title, data, colorFor }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return null;
  return (
    <div style={{ ...card, padding: '18px 20px' }}>
      <div style={{ ...label, marginBottom: 12 }}>{title}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {entries.map(([k, n]) => {
          const c = colorFor ? colorFor(k) : NAVY;
          return (
            <span key={k} style={{
              display: 'inline-flex', alignItems: 'center', gap: 7, padding: '5px 11px',
              borderRadius: 999, background: '#F5F6F8', fontSize: 12.5, color: '#374151', fontWeight: 500,
            }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: c }} />
              {k} <b style={{ color: NAVY }}>{n}</b>
            </span>
          );
        })}
      </div>
    </div>
  );
}

const riskColor = (k) => ({ high: RED, medium: AMBER, low: GREEN, none: '#9CA3AF' }[String(k).toLowerCase()] || '#9CA3AF');
const healthColor = (k) => (String(k).toLowerCase() === 'active' ? GREEN : AMBER);
const onboardColor = (k) => (String(k).toLowerCase() === 'onboarded' ? GREEN : '#9CA3AF');

function StatCard({ title, value, sub, color }) {
  return (
    <div style={{ ...card, padding: '20px 22px' }}>
      <div style={{ ...label, marginBottom: 10 }}>{title}</div>
      <div style={{ fontSize: 34, fontWeight: 700, lineHeight: 1, color: color || NAVY }}>{value}</div>
      {sub && <div style={{ fontSize: 12.5, color: '#6B7280', marginTop: 9 }}>{sub}</div>}
    </div>
  );
}

function ConnectPanel({ status }) {
  return (
    <div style={{ ...card, padding: '40px 32px', maxWidth: 720 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <div style={{ width: 46, height: 46, borderRadius: 12, background: '#F4F6F9', color: NAVY, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.7" viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
        </div>
        <div style={{ fontSize: 17, fontWeight: 700, color: NAVY }}>Microsoft Defender not connected yet</div>
      </div>
      <p style={{ fontSize: 13.5, color: '#4B5563', lineHeight: 1.6, marginTop: 0 }}>
        Once an Entra ID app registration is created and its credentials are added to the
        server, this tab will show the health of every onboarded device — status, risk,
        exposure, and last-seen. Cloud target: <b>{(status?.cloud || 'gcc').toUpperCase()}</b>.
      </p>
      <ol style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.7, paddingLeft: 18, margin: '10px 0 0' }}>
        <li>Register an Entra ID app (single tenant); copy the tenant ID + client ID.</li>
        <li>Add a client secret.</li>
        <li>Grant admin consent to <b>WindowsDefenderATP → Machine.Read.All</b> (application).</li>
        <li>Send the three values to your admin to wire in — then refresh this page.</li>
      </ol>
      <div style={{ marginTop: 16, fontSize: 11.5, color: '#9CA3AF' }}>
        Tenant {status?.tenant_set ? '✓' : '—'} · Client ID {status?.client_set ? '✓' : '—'} · Secret {status?.secret_set ? '✓' : '—'}
      </div>
    </div>
  );
}

export default function DefenderEndpoint() {
  const [status, setStatus] = useState(null);
  const [overview, setOverview] = useState(null);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Default to the real managed fleet; toggle to the full discovered inventory.
  const [scope, setScope] = useState('managed');

  const load = (sc = scope) => {
    setLoading(true); setError(null);
    const q = `?scope=${sc}`;
    axios.get(API + '/defender/overview' + q, { headers: headers() })
      .then(r => {
        setOverview(r.data);
        if (r.data?.configured) {
          return axios.get(API + '/defender/devices' + q, { headers: headers() })
            .then(d => setDevices(d.data?.devices || []));
        }
      })
      .catch(e => setError(e.response?.data?.error || 'Failed to load Defender data'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    axios.get(API + '/defender/status', { headers: headers() }).then(r => setStatus(r.data)).catch(() => {});
    load(scope);
  }, [scope]);

  if (loading && !overview) {
    return <div style={{ padding: 40, color: '#9CA3AF', fontSize: 14 }}>Loading device health…</div>;
  }

  if (overview && overview.configured === false) {
    return (
      <div>
        <SectionHeader title="Endpoint Security" subtitle="Microsoft Defender for Endpoint — device health." />
        <ConnectPanel status={status} />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <SectionHeader title="Endpoint Security" subtitle="Microsoft Defender for Endpoint — device health."
          right={<button onClick={load} style={{ padding: '8px 14px', borderRadius: 9, border: `1px solid ${NAVY}`, background: '#fff', color: NAVY, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Retry</button>} />
        <div style={{ ...card, padding: 32, color: RED, fontSize: 14 }}>{error}</div>
      </div>
    );
  }

  const s = overview?.summary || {};
  const pctHealthy = s.total ? Math.round((s.healthy / s.total) * 100) : 0;
  const counts = overview?.counts || {};

  const toggle = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ display: 'inline-flex', background: '#F1F2F4', borderRadius: 10, padding: 3 }}>
        {[{ k: 'managed', t: 'Managed', n: counts.managed }, { k: 'all', t: 'All discovered', n: counts.all }].map(o => {
          const on = scope === o.k;
          return (
            <button key={o.k} onClick={() => setScope(o.k)}
              style={{
                padding: '6px 13px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12.5,
                fontWeight: on ? 700 : 500, background: on ? '#fff' : 'transparent', color: on ? NAVY : '#6B7280',
                boxShadow: on ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
              }}>
              {o.t}{o.n != null ? ` (${o.n})` : ''}
            </button>
          );
        })}
      </div>
      <button onClick={() => load()} style={{ padding: '8px 14px', borderRadius: 9, border: `1px solid ${NAVY}`, background: '#fff', color: NAVY, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Refresh</button>
    </div>
  );

  return (
    <div>
      <SectionHeader
        title="Endpoint Security"
        subtitle={scope === 'managed'
          ? 'Microsoft Defender for Endpoint — your onboarded devices.'
          : 'Microsoft Defender for Endpoint — full discovered inventory (incl. unmanaged & network devices).'}
        right={toggle}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 16, marginBottom: 18 }}>
        <StatCard title="Devices" value={s.total ?? '—'} sub="in Defender inventory" />
        <StatCard title="Active" value={s.healthy ?? '—'} sub={`${pctHealthy}% reporting to Defender`} color={GREEN} />
        <StatCard title="Needs attention" value={s.needsAttention ?? '—'} sub="inactive / impaired / no data" color={(s.needsAttention || 0) > 0 ? AMBER : NAVY} />
        <StatCard title="Stale" value={s.stale ?? '—'} sub="not seen in 7+ days" color={(s.stale || 0) > 0 ? AMBER : NAVY} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16, marginBottom: 22 }}>
        <Breakdown title="Health status" data={s.byHealth} colorFor={healthColor} />
        <Breakdown title="Risk level" data={s.byRisk} colorFor={riskColor} />
        <Breakdown title="Exposure level" data={s.byExposure} colorFor={riskColor} />
        <Breakdown title="Onboarding" data={s.byOnboarding} colorFor={onboardColor} />
        <Breakdown title="Operating system" data={s.byOs} />
      </div>

      <div style={{ ...card, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #EDEDF0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>Devices</div>
          {overview?.updated_at && <div style={{ fontSize: 11.5, color: '#9CA3AF' }}>Updated {new Date(overview.updated_at).toLocaleTimeString()}</div>}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#8A8A8E', fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                <th style={{ padding: '11px 20px' }}>Device</th>
                <th style={{ padding: '11px 12px' }}>OS</th>
                <th style={{ padding: '11px 12px' }}>Health</th>
                <th style={{ padding: '11px 12px' }}>Risk</th>
                <th style={{ padding: '11px 12px' }}>Exposure</th>
                <th style={{ padding: '11px 20px' }}>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {devices.length === 0 && (
                <tr><td colSpan={6} style={{ padding: '28px 20px', color: '#9CA3AF', textAlign: 'center' }}>No devices returned.</td></tr>
              )}
              {devices.map(d => (
                <tr key={d.id} style={{ borderTop: '1px solid #F1F2F4' }}>
                  <td style={{ padding: '11px 20px', fontWeight: 600, color: NAVY }}>{d.name}</td>
                  <td style={{ padding: '11px 12px', color: '#4B5563' }}>{d.osPlatform}{d.osVersion ? ` ${d.osVersion}` : ''}</td>
                  <td style={{ padding: '11px 12px' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: healthColor(d.healthStatus) }} />
                      {d.healthStatus}
                    </span>
                  </td>
                  <td style={{ padding: '11px 12px', color: riskColor(d.riskScore), fontWeight: 600 }}>{d.riskScore}</td>
                  <td style={{ padding: '11px 12px', color: riskColor(d.exposureLevel), fontWeight: 600 }}>{d.exposureLevel}</td>
                  <td style={{ padding: '11px 20px', color: '#4B5563' }}>
                    {d.lastSeen ? new Date(d.lastSeen).toLocaleDateString() : '—'}
                    {d.daysSinceSeen != null && d.daysSinceSeen > 7 && <span style={{ color: AMBER, marginLeft: 6, fontSize: 11.5 }}>({d.daysSinceSeen}d)</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
